import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import api, { userAPI, authAPI } from '../api'
import type { User, CreditStatus } from '../types'
import axios from 'axios'
import { useSessionTimer } from '../hooks/useSessionTimer'
import SessionWarningModal from '../features/auth/SessionWarningModal'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  isImpersonating: boolean
  credit: CreditStatus | null
  login: (userData: User, sessionExpiresAt?: number) => void
  logout: () => void
  updateUser: (userData: User) => void
  exitImpersonation: () => void
  refreshCredit: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [credit, setCredit] = useState<CreditStatus | null>(null)

  // Handle session expiry - clear auth and redirect to landing page with banner
  const handleSessionExpire = useCallback(async () => {
    console.info('Session expired proactively after 4 hours')
    // Clear session timer from localStorage to prevent redirect loop
    localStorage.removeItem('session_expires_at')
    // Clear state immediately to prevent flash of stale UI before redirect
    setUser(null)
    setIsAuthenticated(false)
    setIsImpersonating(false)
    setShowWarningModal(false)

    // Call backend logout to invalidate JWT (important for impersonation tokens)
    try {
      await authAPI.logout()
    } catch (error) {
      // Ignore errors - we're logging out anyway
      console.debug('Logout call during expiry failed (expected):', error)
    }

    // Hard redirect - state will be fresh on page reload
    window.location.href = '/?banner=session-expired'
  }, [])

  // Handle session warning - show modal 5 minutes before expiry
  const handleSessionWarning = useCallback(() => {
    console.info('Session warning triggered')
    setShowWarningModal(true)
  }, [])

  // Initialize session timer hook
  const { startSession, clearSession, remainingSeconds } = useSessionTimer(
    isAuthenticated,
    handleSessionExpire,
    handleSessionWarning
  )

  // Extend session by calling backend API
  const extendSession = useCallback(async () => {
    try {
      const response = await api.post('/auth/extend-session')
      const newExpiresAt = response.data.sessionExpiresAt

      console.info('Session extended successfully', { newExpiresAt })

      // Restart session timer with new expiry time
      startSession(newExpiresAt)
      setShowWarningModal(false)
    } catch (error) {
      console.error('Failed to extend session:', error)
      throw error
    }
  }, [startSession])

  // Check if user is already logged in when app starts
  useEffect(() => {
    let isMounted = true // Prevent state updates if component unmounts

    const checkAuth = async () => {
      try {
        // Try to get user profile - JWT is in httpOnly cookie
        const response = await userAPI.getProfile()
        if (isMounted) {
          setUser(response.user || response as unknown as User)
          setIsAuthenticated(true)
          setIsImpersonating(response.impersonating || false)
          setCredit(response.credit || null)
        }
      } catch (error: unknown) {
        // Silently fail on 401 (user not logged in)
        if (axios.isAxiosError(error)) {
          const status = error.response?.status
          if (isMounted && status !== 401) {
            console.warn('Auth check failed:', error)
          }
        }
        if (isMounted) {
          setUser(null)
          setIsAuthenticated(false)
          setIsImpersonating(false)
          setCredit(null)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    checkAuth()

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false
    }
  }, [])

  // Check session validity when tab becomes visible (detects stale sessions)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only check if tab became visible and user was authenticated
      if (!document.hidden && isAuthenticated) {
        try {
          // Silently verify session is still valid
          await userAPI.getProfile()
          // Session still valid - no action needed
        } catch (error) {
          // 401 will be handled by api interceptor
          console.debug('Session check failed on visibility change:', error)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated])

  // Handle successful login
  const login = (userData: User, sessionExpiresAt?: number) => {
    setUser(userData)
    setIsAuthenticated(true)
    // Start session timer with expiry timestamp from backend
    if (sessionExpiresAt) {
      startSession(sessionExpiresAt)
    }
  }

  // Handle logout - call backend to clear httpOnly cookie
  const logout = useCallback(async () => {
    // Clear session timer
    clearSession()
    try {
      await authAPI.logout()
    } catch (error) {
      console.warn('Logout API call failed:', error)
      // Continue with local logout even if API call fails
    }
    setUser(null)
    setIsAuthenticated(false)
    setIsImpersonating(false)
    setCredit(null)
    // Redirect to landing page with success banner
    window.location.href = '/?banner=logout-success'
  }, [clearSession])

  // Update user data (e.g., after profile update)
  const updateUser = (userData: User) => {
    setUser(userData)
  }

  // Refresh credit status from backend
  const refreshCredit = useCallback(async () => {
    try {
      const response = await userAPI.getProfile()
      setCredit(response.credit || null)
    } catch (error) {
      console.error('Failed to refresh credit:', error)
    }
  }, [])

  // Exit impersonation and return to admin dashboard
  const exitImpersonation = useCallback(async () => {
    try {
      // Call backend to restore admin session
      const response = await api.post('/admin/exit-impersonation')

      // Update state with admin user
      setUser(response.data.user)
      setIsImpersonating(false)

      // Redirect to admin dashboard
      window.location.href = '/admin-portal'
    } catch (error) {
      console.error('Exit impersonation failed:', error)
      // Fallback: logout and redirect
      await logout()
      window.location.href = '/admin-portal'
    }
  }, [logout])

  const value = {
    user,
    isAuthenticated,
    loading,
    isImpersonating,
    credit,
    login,
    logout,
    updateUser,
    exitImpersonation,
    refreshCredit
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Don't show warning modal during impersonation - timer is visible in banner instead */}
      {!isImpersonating && (
        <SessionWarningModal
          show={showWarningModal}
          remainingSeconds={remainingSeconds}
          onExtendSession={extendSession}
          onLogout={logout}
          onHide={() => setShowWarningModal(false)}
        />
      )}
    </AuthContext.Provider>
  )
}

// Custom hook to use the auth context
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
